import {BaseClient, DataType} from "./BaseClient";
import {ISODateRange, LondonDateRange} from "../ISODateRange";
import {DateTime} from "../time/DateTime";
import {baseLineMethodology} from "../components/BillingExplanation";
import {Result} from "./Result";
import {TimeRange} from "../components/common/TimeRangePicker";

export class DateRange {
    public readonly start: DateTime
    public readonly end: DateTime

    constructor(start: DateTime, end: DateTime) {
        this.start = start
        this.end = end
    }

    public contains(dateTime: DateTime): boolean {
        return dateTime.toMillis() >= this.start.toMillis() && dateTime.toMillis() <= this.end.toMillis()
    }
}

export interface ApiPowerEntry {
    time: string
    powerInKilowatts: number
}

export interface PowerEntry {
    date: number,
    power: number
}

export interface HalfHourUploadFormats {
    headerFields: string[],
    footer?: string,
    timeZone: string
}

export function apiPowerEntryToPowerEntry(apiReading: ApiPowerEntry): PowerEntry {
    return {date: DateTime.fromISO(apiReading.time).toUnixInteger(), power: apiReading.powerInKilowatts / 1000.0}
}

export type ReportType = 'byBaseline' | 'byDrop';


export interface BaselineEntry {
    intervals: BaselineInterval[]
}

export interface BaselineInterval {
    from: string
    to: string
    baselineInKilowatts: number
    dropInKilowattsHour: number
}

export class DataClient {

    baseClient: BaseClient

    constructor(baseClient: BaseClient) {
        this.baseClient = baseClient
    }

    async getFormatsForUpload(): Promise<Result<HalfHourUploadFormats[]>> {
        return (await this.baseClient.getOkP("/../etl/api/hhConsumption/format"))
    }

    async powers(buildingId: number, range: DateRange): Promise<PowerEntry[]> {
        const url = `/api/power` +
                `?buildingId=${buildingId}` +
                `&startTime=${range.start.toUTC().toISO()}` +
                `&endTime=${range.end.toUTC().toISO()}` +
                `&granularity=PT5M`
        const apiReadings = await this.baseClient.getOk<ApiPowerEntry[]>(url)
        return apiReadings.map(apiPowerEntryToPowerEntry)
    }

    async readingsUKPNSettlement(buildingId: number, range: DateRange): Promise<Result<Blob>> {
        const url = `/api/readings` +
                `?buildingId=${buildingId}` +
                `&startTime=${range.start.toUTC().toISO()}` +
                `&endTime=${range.end.toUTC().toISO()}` +
                `&variant=ukpnSettlement`
        return this.baseClient.getOkBlob(url, { 'Accept': 'text/csv' });
    }

    async eventStats(buildingId: number, range: DateRange): Promise<Result<Blob>> {
        const url = `/api/eventStats` +
            `?buildingId=${buildingId}` +
            `&from=${range.start.toUTC().toISO()}` +
            `&to=${range.end.toUTC().toISO()}`
        return this.baseClient.getOkBlob(url, { 'Accept': 'application/octet-stream' })
    }

    private getDrop(range: ISODateRange, timeRange: TimeRange | undefined , reportType: string , selectorId: string ) {
        const url = `/api/highestCapacityMarketDrops` +
            `?baselineStrategy=precalculated` +
            selectorId +
            `&start=${range.start}` +
            `&end=${range.end}` +
            `&baselineReport=${reportType}` +
            `&limit=10`

        const fu = timeRange ? url + "&startTime=" + timeRange?.startTime?.asDateString() + "&endTime=" + timeRange?.endTime?.asDateString() : url

        return this.baseClient.getOkBlob(fu, { 'Accept': 'text/csv' });
    }

    async capacityMarketDropReportByCmu(cmuId: string, reportType: ReportType, range: ISODateRange, timeRange: TimeRange|undefined) {
        return this.getDrop(range,timeRange,reportType,`&cmuId=${cmuId}`);
    }

    async capacityMarketDropReportByBucket(bucketId: string, reportType: ReportType, range: ISODateRange, timeRange: TimeRange|undefined): Promise<Result<Blob>> {
        return this.getDrop(range,timeRange,reportType,`&bucket=${bucketId}`);
    }

    async capacityMarketEmrComponentReport(cmuId: string): Promise<Result<Blob>> {
        const url = `/api/cmEmrComponentReport` +
            `?cmuId=${cmuId}`
        return this.baseClient.getOkBlob(url, { 'Accept': 'text/csv' });
    }

    async capacityMarketEmrsMeteringPointComponentReport(cmuId: string): Promise<Result<Blob>> {
        const url = `/api/cmEmrsComponentMeteringPointReport` +
            `?cmuId=${cmuId}`
        return this.baseClient.getOkBlob(url, { 'Accept': 'text/csv' });
    }

    async uploadReadings(file: File): Promise<Result<null>> {
        const url = `/../etl/api/halfHourlyConsumption`
        return this.baseClient.postOk(url, file, DataType.FORM_DATA);
    }

    async uploadBucket(file: File, bucketId:string): Promise<Result<null>> {
        const url = `/api/updateBucketMpans?programme=CapacityMarket&bucketId=${bucketId}`
        return this.baseClient.postOk(url, file, DataType.FORM_DATA);
    }

    async retrieveMeterHalfHourlyConsumptionForMpan(mpan: string, range: ISODateRange, format:string): Promise<Result<Blob>> {
        const allParams: { mpan: string; start: string; end: string; format: string; timeZone: string } =
            {mpan: mpan, start: range.start, end: range.end, format: format, timeZone: 'Europe/London'}
        const queryParams = new URLSearchParams(allParams)
        return this.baseClient.getOkBlob('/api/halfHourlyConsumption?' + queryParams.toString(), { 'Accept': 'text/csv' });
    }

    async retrieveMeterHalfHourlyConsumptionForBucketInElexonFormatForDsrCertificate(cmuId: string, range: ISODateRange): Promise<Result<Blob>> {
        const allParams: { cmuId: string; start: string; end: string; format: string; timeZone: string } =
            {cmuId: cmuId, start: range.start, end: range.end, format: 'EMRS', timeZone: 'Europe/London'}
        const queryParams = new URLSearchParams(allParams)
        return this.baseClient.getOkBlob('/api/halfHourlyConsumption?' + queryParams.toString(), { 'Accept': 'application/octet-stream' })
    }

    async retrieveBillingExport(range: ISODateRange): Promise<Result<Blob>> {
        const api = `/api/billing/invoice/start/${range.start}/end/${range.end}`
        return this.baseClient.getOkBlob(api, { 'Accept': 'text/csv' });
    }

    async retrieveDfsSettlement(londonRange: LondonDateRange) {
        const api = `/api/billing/dfsSettlementReport?from=${londonRange.range.start.toUTC().toISO()}&to=${londonRange.range.end.toUTC().toISO()}`
        return this.baseClient.getOkBlob(api, { 'Accept': 'text/csv' });
    }

    async retrieveBillingExplanation(buildingId: number, range: ISODateRange, methodology: baseLineMethodology): Promise<Result<Blob>> {
        return (methodology == 'OldPreviousHhAverage')
            ? this.retrieveBillingExplanationHalfHour(buildingId, range)
            : this.retrieveBillingExplanationHistoricBaseline(buildingId, new LondonDateRange(range.start, range.end), methodology)
    }

    private async retrieveBillingExplanationHalfHour(buildingId: number, range: ISODateRange): Promise<Result<Blob>> {
        const api = `/api/billing/events/start/${range.start}/end/${range.end}/building/${buildingId}`
        return this.baseClient.getOkBlob(api, { 'Accept': 'text/csv' });
    }

    private async retrieveBillingExplanationHistoricBaseline(buildingId: number, londonRange: LondonDateRange, methodology: baseLineMethodology): Promise<Result<Blob>> {
        const api = `/api/billing/baseline?buildingId=${buildingId}&from=${londonRange.range.start.toUTC().toISO()}&to=${londonRange.range.end.toUTC().toISO()}&methodology=${methodology}`
        return this.baseClient.getOkBlob(api, { 'Accept': 'text/csv' });
    }

    async retrieveBaseline(buildingId: number, range: DateRange, methodology: baseLineMethodology): Promise<Result<BaselineInterval[]>> {
        const api = `/api/billing/baseline?buildingId=${buildingId}&from=${range.start.toUTC().toISO()}&to=${range.end.toUTC().toISO()}&methodology=${methodology}`
        const result = await this.baseClient.getOkP<BaselineEntry[]>(api)
        return result.map( a => a.flatMap(e => e.intervals))
    }
}
